// The vocabulary of a research review — T53 (12 Sep 2026), the agents doc § 6.2 and § 6.4.
//
// ONE HOME for the reject pick-list, because three things read it: the brain draws it as
// buttons, the BFF refuses a reason outside it, and `/researchlog` groups rejections by it
// before it groups by the recruiter's own words. A reason added here reaches all three.
//
// The list is the agents doc's, verbatim: "wrong seniority, wrong sector, wrong location, works
// at one of our clients, already spoken to, other". `other` is what the free-text box is for;
// the rule session reads the words behind it.
//
// Its own entry point (`@vera/db/research-review`), like `client-status` and `match-key`, so the
// brain can import it without loading the package root and its driver.
import { requirementQuestions, type RoleFamily } from './questions.ts';

export const REJECT_REASONS = [
  'wrong_seniority',
  'wrong_sector',
  'wrong_location',
  'works_at_client',
  'already_spoken_to',
  'other',
] as const;

export type RejectReason = (typeof REJECT_REASONS)[number];

/** How the brain spells each reason to a recruiter. */
export const REJECT_REASON_LABELS: Readonly<Record<RejectReason, string>> = {
  wrong_seniority: 'Wrong seniority',
  wrong_sector: 'Wrong sector',
  wrong_location: 'Wrong location',
  works_at_client: 'Works at one of our clients',
  already_spoken_to: 'Already spoken to',
  other: 'Other',
};

export const isRejectReason = (value: string): value is RejectReason =>
  (REJECT_REASONS as readonly string[]).includes(value);

/**
 * T126a (5 Oct 2026), the fit doc § 9.9: a rejection may also name the questions the person failed
 * — "AI depth too low" a key the decisions block counts and `/researchlog` groups by. Offered from
 * the Requirements card's set for the Search's kind of role (`requirementQuestions`), the Search's
 * own requirements first, each in Mona's asking order (Natanel, 5 Oct 2026: "Search's first").
 */
export interface RejectKey {
  key: string;
  label: string;
  /** A question the Search holds a requirement on: shown first, the rest behind "More". */
  onSearch: boolean;
}

export function rejectKeyOffer(
  family: RoleFamily | null,
  requirementKeys: readonly string[],
): RejectKey[] {
  const on = new Set(requirementKeys);
  const all = requirementQuestions(family).map((q) => ({
    key: q.key,
    label: q.label,
    onSearch: on.has(q.key),
  }));
  return [...all.filter((k) => k.onSearch), ...all.filter((k) => !k.onSearch)];
}

/** The most questions one rejection may name: a reason, not the whole card. */
export const MAX_REASON_KEYS = 5;

/**
 * A rejection's questions as sent, checked against the Search's offer: each a key offered, none
 * twice, at most `MAX_REASON_KEYS`. The keys in the order sent, or the refusal in words.
 */
export function readReasonKeys(
  raw: unknown,
  offer: readonly RejectKey[],
): { ok: true; keys: string[] } | { ok: false; reason: string } {
  if (raw === undefined || raw === null) return { ok: true, keys: [] };
  if (!Array.isArray(raw) || raw.some((k) => typeof k !== 'string')) {
    return { ok: false, reason: 'reason_keys must be a list of question keys' };
  }
  const keys = [...new Set(raw as string[])];
  if (keys.length > MAX_REASON_KEYS) {
    return { ok: false, reason: `reason_keys names at most ${MAX_REASON_KEYS} questions` };
  }
  const offered = new Set(offer.map((k) => k.key));
  const outside = keys.filter((k) => !offered.has(k));
  if (outside.length > 0) {
    return {
      ok: false,
      reason: `reason_keys: ${outside.join(', ')} is not a question this Search's kind of role is asked`,
    };
  }
  return { ok: true, keys };
}

/** The three decisions a review row may carry — `research_reviews.decision`'s CHECK. */
export const REVIEW_DECISIONS = ['accepted', 'rejected', 'miss'] as const;

export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

/**
 * How a miss is classified, the agents doc § 6.4 — what the tab can say from the run's own rows
 * and what only a rule session with the provider can settle:
 *
 *  - `found_and_dropped`: the run pulled this person and the agent judged them not a fit
 *    (a `dropped` row matched them). A rule about what to keep.
 *  - `not_reached`: the run's queries did not surface them. Either a rule about how to search
 *    or a provider gap — which of the two is the session's lookup (`not_in_provider`).
 *  - `not_in_provider`: settled by `/researchlog` looking the person up directly.
 */
export const MISS_KINDS = ['found_and_dropped', 'not_reached', 'not_in_provider'] as const;

export type MissKind = (typeof MISS_KINDS)[number];

/** The longest note or free-text reason the brain accepts. A sentence or two, not an essay. */
export const MAX_REVIEW_NOTE = 1_000;

/** The longest ask a run may carry: the role brief plus what the recruiter added. */
export const MAX_RESEARCH_ASK = 2_000;

/**
 * T53e (30 Sep 2026): whether a Search says enough to be researched — a role brief, or at least
 * one requirement. The first answer-key run's rule (agents doc § 6.5): a title alone swept in a
 * municipality's technical director and a bakery's. The New research form and the write share it.
 * T360 E1 (Natanel, 16 Oct 2026, "Research runs on your words"): the brain's agent may also start
 * a run on a Search with neither, when the run's own ask is given — pass `ask` for that door only.
 * The form pre-fills its ask with the Search title, so it never passes one: its rule stays.
 */
export const researchable = (
  roleBrief: string | null | undefined,
  requirements: readonly unknown[],
  ask?: string,
): boolean =>
  (roleBrief?.trim() ?? '') !== '' || requirements.length > 0 || (ask?.trim() ?? '') !== '';

/**
 * How many people a run is asked to propose — T161 (28 Sep 2026). Natanel asked the brain for 20
 * and got 3: every run collected the trial's eight records and proposed whoever of them fitted.
 * A run now collects until it has this many proposals or reaches its ceiling. `research_runs.wanted`'s
 * CHECK holds the same bounds (migration 0103); the brain's field and the agent's tool are drawn
 * from these.
 */
export const RESEARCH_WANTED = { min: 1, max: 50, default: 10 } as const;

/**
 * T161: people a run may collect for each one it is asked for — its ceiling, within the service's
 * `RESEARCH_MAX_RECORDS` (`@vera/agent`'s `recordCeiling`). Four until T364 (13 Oct 2026): the four
 * production runs before T161 proposed 14 of the 32 people they collected, about one in two. Eight
 * since Natanel's pick ("8 per person asked"), because a run asked for a number now searches wider
 * when its first searches run dry, and wider searches read more people who fit less. A Search where
 * fewer fit stops at the ceiling and says so rather than spending on. Twenty credits a person on
 * Coresignal: a run asked for 20 spends 3,200 at most.
 */
export const RESEARCH_RECORDS_PER_PERSON = 8;

/**
 * Why a run proposed fewer people than it was asked for — T161, `research_run_results.stopped_short`
 * (its CHECK, migration 0103), null when it found them all:
 *
 *  - `ceiling`: it collected as many people as one run may for that number, and fewer fitted.
 *  - `no_more_people`: the searches found nobody else it had not collected or already proposed
 *    on this Search.
 *  - `provider_failed`: the provider stopped answering partway; what it collected was judged.
 *  - `model_failed`: the model failed partway: one batch's judgement, or a wider search, could not
 *    be used; what the run had found stands.
 *  - `below_floor` (T402): a run with a floor read everyone its searches found, short of the number
 *    at the floor, and the others it read are under it (the run's page lists them apart).
 */
export const RESEARCH_STOPS = [
  'ceiling',
  'no_more_people',
  'provider_failed',
  'model_failed',
  'below_floor',
] as const;

export type ResearchStop = (typeof RESEARCH_STOPS)[number];

export const isResearchStop = (value: string): value is ResearchStop =>
  (RESEARCH_STOPS as readonly string[]).includes(value);

/**
 * A short run in one sentence, for the tab and for the brain's agent: "Found 14 of the 20 asked
 * for: the searches found nobody else." `read` is the people the run collected. A run from before
 * T161 has no number and gets no sentence.
 */
export function shortfallSentence(
  proposed: number,
  wanted: number,
  stop: ResearchStop,
  read: number,
  floor: number | null = null,
): string {
  const why: Record<ResearchStop, string> = {
    ceiling: `it read ${read} people, the most one run reads for ${wanted}, and ${proposed === 0 ? 'none' : 'the others did not'} fit`,
    no_more_people: 'the searches found nobody else',
    provider_failed: 'the people provider stopped answering partway',
    model_failed: 'the model failed partway',
    below_floor: `the searches ran dry and the others it read are below ${floor ?? 7}, listed on the run's page`,
  };
  const at = floor === null ? '' : ` at ${floor} or above`;
  return `Found ${proposed} of the ${wanted} asked for${at}: ${why[stop]}.`;
}

/**
 * How long a finished run may wait before the Research tab offers to close it — T404 (19 Oct
 * 2026), Natanel: "Close runs older than a week". The button's count and the route share it.
 */
export const RESEARCH_CLOSE_OLDER_DAYS = 7;

/**
 * T404: the people of one run that wait on a recruiter — the run's proposals not yet decided, and
 * none at all once a recruiter closed the run. The one place the subtraction lives: the tab, the
 * WhatsApp nudges and the morning brief read `counts.toDecide`, which is this.
 */
export const toDecideOf = (run: { closed: boolean; proposals: number; decided: number }): number =>
  run.closed ? 0 : Math.max(0, run.proposals - run.decided);

/**
 * T404: whether "Close runs older than a week" closes this run — finished more than
 * `RESEARCH_CLOSE_OLDER_DAYS` days before `now`, not closed already, with someone still to decide.
 */
export const closesAsOlder = (
  run: { status: string; closed: boolean; toDecide: number; finishedAt: string | null },
  now: Date,
): boolean =>
  run.status === 'done' &&
  !run.closed &&
  run.toDecide > 0 &&
  run.finishedAt !== null &&
  now.getTime() - new Date(run.finishedAt).getTime() >
    RESEARCH_CLOSE_OLDER_DAYS * 24 * 60 * 60 * 1000;

/**
 * T126h (26 Oct 2026): what kadmon's research does with a saved note. `decision`: the note names a
 * ground the screen holds (health, age, origin and the like), so the whole decision is left out.
 * `note`: the screen could not check it (the model check did not answer, or a note saved before
 * T577 in a script the word floor cannot read), so the decision counts and the note is dropped.
 * Null: read as written. `noteHeld` (the agent package) is the one place that decides; the brain
 * and the brain agent say it with `noteHeldLine`.
 */
export type NoteHeld = 'decision' | 'note';

/**
 * T577 (31 Oct 2026, Natanel: "A model checks every note"): what the model check said of a saved
 * note, stored beside it (`research_reviews.note_screen`). `read`: about the work, research may
 * learn from it. `held`: it turns the person down, or picks them, on a protected ground. `unread`:
 * the check could not answer ("Note unread, decision kept"). A column of null is a note saved before T577, never checked; a new note is `unread` until
 * its verdict lands.
 */
export type NoteScreen = 'read' | 'held' | 'unread';

/** The column as written (its check holds these three), or null: unchecked, or anything else. */
export const readNoteScreen = (raw: string | null): NoteScreen | null =>
  raw === 'read' || raw === 'held' || raw === 'unread' ? raw : null;

/** T126d: said once a saved note has a word the screen leaves out, so the recruiter knows. */
export const NOTE_HELD_BACK =
  "kadmon's research will not use this decision: its note has a word it leaves out, like one about health, age or origin.";

/**
 * T126h, T577: the note is dropped, the decision kept — the model check could not answer, or an
 * old Hebrew note was saved before every note was checked.
 */
export const NOTE_UNREAD =
  "kadmon's research will use this decision but not its note: it could not check the note.";

export const noteHeldLine = (held: NoteHeld): string =>
  held === 'decision' ? NOTE_HELD_BACK : NOTE_UNREAD;

/** T126h: the two steps that save a note research reads; a result `detail` on either says when it won't. */
export const RESEARCH_NOTE_TOOLS: ReadonlySet<string> = new Set([
  'reject_proposal',
  'note_research_miss',
]);

/** T126h: a card's results carry a held-note line, which a one-line group summary would drop. */
export const resultsTellNoteHeld = (
  results: readonly { tool: string; detail?: string | null }[] | null | undefined,
): boolean => Boolean(results?.some((r) => RESEARCH_NOTE_TOOLS.has(r.tool) && r.detail));
